// Sound effects via Web Audio. WAV samples are Stuart Cheshire's original
// Bolo sounds (© 1993), bundled here non-commercially under the same
// precedent that WinBolo has used them since ~2000. See
// packages/client/public/sounds/CREDITS.txt.
//
// WinBolo's near/far/self variant system: the original game ships three
// recordings of cannon fire (one mixed for "you fired", one for "nearby
// tank fired", one for "distant tank fired") and two of each explosion
// (near/far). We replicate that selection here based on the listener's
// distance to the source — more authentic and cheaper than DSP-attenuating
// a single sample across 80 tiles of map.
//
// All buffers load lazily on the first user gesture. Until then, every
// `play()` is a no-op (the AudioContext can't decode anything while
// suspended anyway).

import { WORLD_UNITS_PER_TILE } from '@bolo2k/shared';

/** Distance (in tiles) below which a shellFire sound counts as "self" — i.e.
 *  fired from a position close enough to the listener that it's the
 *  listener's own tank. Shells appear at the firing tank's position so
 *  this is a reliable proxy for ownership without needing an extra wire
 *  field. */
const SELF_TILES = 1.5;

/** Distance (in tiles) below which a sound uses the "near" variant. Beyond
 *  this we use the "far" mix. */
const NEAR_TILES = 12;

/** Distance (in tiles) at which a sound is inaudible. Beyond this we skip
 *  scheduling entirely so cross-map shootouts don't burn cycles. */
const MAX_AUDIBLE_TILES = 60;

export type SoundEvent =
  | 'shellFire'
  | 'explosionBig'
  | 'explosionSmall'
  | 'bubbles'
  | 'farmingTree'
  | 'shotTree'
  | 'shotBuilding'
  | 'manBuilding'
  | 'manLayMine'
  | 'manDying'
  | 'hitTank'
  | 'tankSinking';

interface VariantSet {
  self?: AudioBuffer;
  near: AudioBuffer;
  far: AudioBuffer;
}

interface PendingVariantSet {
  self?: Promise<AudioBuffer>;
  near: Promise<AudioBuffer>;
  far: Promise<AudioBuffer>;
}

// Maps each SoundEvent to the WAV files Bolo originally used for it. See
// packages/client/public/sounds/CREDITS.txt for licensing context.
const SOUND_FILES: Record<SoundEvent, { self?: string; near: string; far: string }> = {
  shellFire: {
    self: '/sounds/shooting_self.wav',
    near: '/sounds/shooting_near.wav',
    far: '/sounds/shooting_far.wav',
  },
  explosionBig: {
    near: '/sounds/big_explosion_near.wav',
    far: '/sounds/big_explosion_far.wav',
  },
  explosionSmall: {
    near: '/sounds/mine_explosion_near.wav',
    far: '/sounds/mine_explosion_far.wav',
  },
  bubbles: {
    // WinBolo ships a single mono sample; same file for near and far. Played
    // when a wading tank loses ammo to water drain.
    near: '/sounds/bubbles.wav',
    far: '/sounds/bubbles.wav',
  },
  farmingTree: {
    near: '/sounds/farming_tree_near.wav',
    far: '/sounds/farming_tree_far.wav',
  },
  shotTree: {
    near: '/sounds/shot_tree_near.wav',
    far: '/sounds/shot_tree_far.wav',
  },
  shotBuilding: {
    near: '/sounds/shot_building_near.wav',
    far: '/sounds/shot_building_far.wav',
  },
  manBuilding: {
    near: '/sounds/man_building_near.wav',
    far: '/sounds/man_building_far.wav',
  },
  manLayMine: {
    // WinBolo ships only one variant; reuse for far.
    near: '/sounds/man_lay_mine_near.wav',
    far: '/sounds/man_lay_mine_near.wav',
  },
  manDying: {
    near: '/sounds/man_dying_near.wav',
    far: '/sounds/man_dying_far.wav',
  },
  hitTank: {
    self: '/sounds/hit_tank_self.wav',
    near: '/sounds/hit_tank_near.wav',
    far: '/sounds/hit_tank_far.wav',
  },
  tankSinking: {
    near: '/sounds/tank_sinking_near.wav',
    far: '/sounds/tank_sinking_far.wav',
  },
};

export class Audio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private buffers: Partial<Record<SoundEvent, VariantSet>> = {};
  private loading: Partial<Record<SoundEvent, PendingVariantSet>> = {};

  /** Resume the AudioContext after a user gesture. Browsers force-suspend
   *  contexts created without one; calling this from a click handler
   *  unsticks it. Safe to call repeatedly. Triggers WAV loading on first
   *  call so samples are warm by the time the first shell fires. */
  enable(): void {
    if (!this.ctx) {
      const Ctx =
        (globalThis as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
          .AudioContext ??
        (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      this.ctx = new Ctx();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.45;
      this.master.connect(this.ctx.destination);
      // Kick off all sample loads in parallel; misses just play silently
      // until the buffer arrives (typically <100 ms on local network).
      for (const event of Object.keys(SOUND_FILES) as SoundEvent[]) {
        void this.loadEvent(event);
      }
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  /** Play a sound positioned at (worldX, worldY) — distance from `listener`
   *  picks the WinBolo near/far/self variant. Pass listener=null for
   *  spectators (always uses "near"). */
  play(
    event: SoundEvent,
    worldX: number,
    worldY: number,
    listener: { x: number; y: number } | null,
  ): void {
    if (!this.ctx || !this.master) return;
    const dist = listener ? tileDistance(worldX, worldY, listener.x, listener.y) : 0;
    if (dist > MAX_AUDIBLE_TILES) return;
    const variants = this.buffers[event];
    if (!variants) return; // still loading
    const buf = pickVariant(variants, dist);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.master);
    src.start();
  }

  // --- Internals -------------------------------------------------------

  private async loadEvent(event: SoundEvent): Promise<void> {
    if (!this.ctx) return;
    if (this.buffers[event] || this.loading[event]) return;
    const files = SOUND_FILES[event];
    const pending: PendingVariantSet = {
      near: this.loadBuffer(files.near),
      far: this.loadBuffer(files.far),
      ...(files.self ? { self: this.loadBuffer(files.self) } : {}),
    };
    this.loading[event] = pending;
    try {
      const [near, far, self] = await Promise.all([
        pending.near,
        pending.far,
        pending.self ?? Promise.resolve(undefined),
      ]);
      this.buffers[event] = self ? { self, near, far } : { near, far };
    } finally {
      delete this.loading[event];
    }
  }

  private async loadBuffer(url: string): Promise<AudioBuffer> {
    if (!this.ctx) throw new Error('AudioContext not initialised');
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`fetch ${url}: ${resp.status}`);
    const ab = await resp.arrayBuffer();
    return this.ctx.decodeAudioData(ab);
  }
}

function pickVariant(set: VariantSet, distTiles: number): AudioBuffer {
  if (distTiles <= SELF_TILES && set.self) return set.self;
  if (distTiles <= NEAR_TILES) return set.near;
  return set.far;
}

function tileDistance(ax: number, ay: number, bx: number, by: number): number {
  const dx = (ax - bx) / WORLD_UNITS_PER_TILE;
  const dy = (ay - by) / WORLD_UNITS_PER_TILE;
  return Math.hypot(dx, dy);
}
